import { t } from "i18next"
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb"
import { useAuth } from "../../../../core/hooks/useAuth"
import { useHandleCourses } from "../../../../core/hooks/useHandleCourses"
import { useParams } from "react-router-dom"
import { ConfigProvider, Progress, Table, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import { useTranslation } from "react-i18next"
import { useEffect, useMemo, useState } from "react"
import { extractTextAndUrls } from "../../../../core/common/ults/domParser"
import { useTrackList } from "../../../../core/hooks/useTrackVideo"
import {
    buildMediaStatusMap,
    lookupMediaProgress,
} from "../../../../core/common/ults/trackProgress"

// Xem gần hết file được tính là đã xem
const WATCHED_PERCENT_THRESHOLD = 95;

const StudentProgressDetail = () => {
    const { id } = useParams();
    const { moodleUser } = useAuth()
    const { courseContents, courseDetails } = useHandleCourses(id)
    const courseDetailData = courseDetails?.courses[0];
    const { t } = useTranslation();
    const { data: trackList, isLoading } = useTrackList(id, moodleUser);

    //  lấy toàn bộ modules
    const allModules = courseContents?.flatMap((section: any) => section.modules || []) || [];

    // modules hoàn thành
    const countCompletedModulesByType = (modname: string): number => {
        return allModules.filter(
            (module: any) => module.modname === modname && [1, 2].includes(module.completiondata?.state)
        ).length;
    };

    // tính tiến độ
    const getProgressPercent = (completed: number, total: number): number => {
        return total ? Math.round((completed / total) * 100) : 0;
    };

    // Tổng số bài quiz, assign
    const quizModules = allModules.filter((mod: any) => mod.modname === 'quiz' && mod.completion > 0);
    const assignModules = allModules.filter((mod: any) => mod.modname === 'assign' && mod.completion > 0);

    // Số bài quiz, assign hoàn thành
    const completedQuiz = countCompletedModulesByType("quiz");
    const completedAssign = countCompletedModulesByType("assign");

    // Tiến độ quiz
    const quizProgressPercent = getProgressPercent(completedQuiz, quizModules.length);

    // Tiến độ assign
    const assignProgressPercent = getProgressPercent(completedAssign, assignModules.length);

    // đếm các hoạt động khác: chỉ hiển thị Diễn đàn (forum)
    const countOtherModules = allModules.filter((mod: any) => mod.modname === 'forum' && mod.completion > 0);
    const completedOtherModules = countOtherModules.filter((mod: any) => [1, 2].includes(mod.completiondata?.state));
    const otherProgressPercent = getProgressPercent(completedOtherModules.length, countOtherModules.length);


    // tiến độ bài tập (quiz + assign)
    const exerciseProgressPercent = getProgressPercent(
        completedQuiz + completedAssign,
        quizModules.length + assignModules.length
    );

    // danh sách theo loại modules
    const formatModules = (modules: any[], group: string) =>
        modules.map((mod: any, index: number) => ({
            key: `${group}-${mod.modname}-${index}`,
            name: mod.name,
            status: [1, 2].includes(mod.completiondata?.state) ? t("progress.completed") : t("progress.notCompleted"),
            group,
        }));

    // danh sách theo các modules khác
    const formatOtherModules = (modules: any[]) =>
        modules.map((mod: any, index: number) => ({
            key: `other-${mod.modname}-${index}`,
            name: mod.name,
            status: [1, 2].includes(mod.completiondata?.state) ? t("progress.completed") : t("progress.notCompleted"),
        }));


    const quizFormatted = formatModules(quizModules, t("progress.quizGroup"));
    const assignFormatted = formatModules(assignModules, t("progress.assignmentGroup"));


    const getMaterialFileType = (url: string, type?: string): "video" | "audio" | "pdf" | "unknown" => {
        if (type === "video" || type === "audio" || type === "pdf") return type;
        const lowerUrl = (url || "").toLowerCase().split("?")[0];
        if (lowerUrl.endsWith(".pdf")) return "pdf";
        if (
            lowerUrl.endsWith(".mp3") ||
            lowerUrl.endsWith(".wav") ||
            lowerUrl.endsWith(".m4a") ||
            lowerUrl.endsWith(".aac")
        ) {
            return "audio";
        }
        if (
            lowerUrl.endsWith(".mp4") ||
            lowerUrl.endsWith(".webm") ||
            lowerUrl.endsWith(".ogg") ||
            url.includes("youtube") ||
            url.includes("vimeo")
        ) {
            return "video";
        }
        return "unknown";
    };

    const mediaStatusMap = useMemo(
        () => buildMediaStatusMap(trackList),
        [trackList]
    );

    const getMediaInfoFromMap = (mediaUrl: string) => {
        const entry = lookupMediaProgress(mediaStatusMap, mediaUrl);

        if (!entry) {
            return { status: t("progress.notWatched"), percent: 0, filetype: null };
        }

        const rawPercent = entry.percent || 0;
        const isWatched =
            entry.status === "completed" ||
            rawPercent >= WATCHED_PERCENT_THRESHOLD;
        const label = isWatched
            ? t("progress.watched")
            : entry.status === "inProgress"
            ? t("progress.watching")
            : t("progress.notWatched");

        return {
            status: label,
            percent: isWatched ? 100 : rawPercent,
            filetype: entry.filetype || null,
        };
    };

    const isTrackableMedia = (url: string, type?: string) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase().split("?")[0];
        if (type === "video" || type === "audio" || type === "pdf") return true;
        return (
            lowerUrl.endsWith(".mp4") ||
            lowerUrl.endsWith(".webm") ||
            lowerUrl.endsWith(".ogg") ||
            lowerUrl.endsWith(".mp3") ||
            lowerUrl.endsWith(".wav") ||
            lowerUrl.endsWith(".m4a") ||
            lowerUrl.endsWith(".aac") ||
            lowerUrl.endsWith(".pdf") ||
            url.includes("youtube") ||
            url.includes("vimeo")
        );
    };

    const weekData = (courseContents || [])
        .filter((section: any) => section.section > 0)
        .map((section: any, index: number) => {
            const sessions: {
                key: string;
                name: string;
                items: {
                    name: string;
                    url: string;
                    status?: string;
                    percent?: number;
                    filetype?: string;
                }[];
            }[] = [];

            section.modules?.forEach((mod: any, lessonIndex: number) => {
                const pushItem = (
                    list: {
                        name: string;
                        url: string;
                        status?: string;
                        percent?: number;
                        filetype?: string;
                    }[],
                    name: string,
                    url: string,
                    type?: string
                ) => {
                    const filetype = getMaterialFileType(url, type);
                    const info = getMediaInfoFromMap(url);
                    list.push({
                        name,
                        url,
                        filetype,
                        status: info.status,
                        percent: filetype === "pdf" ? undefined : info.percent,
                    });
                };

                if (mod.modname !== "unilabel") return;

                const { title, items } = extractTextAndUrls(mod.description);
                const mediaItems: {
                    name: string;
                    url: string;
                    status?: string;
                    percent?: number;
                    filetype?: string;
                }[] = [];

                items?.forEach((res: any) => {
                    if (isTrackableMedia(res.url, res.type)) {
                        pushItem(mediaItems, res.text, res.url, res.type);
                    }
                });

                if (mediaItems.length > 0) {
                    sessions.push({
                        key: `${section.id || index}-session-${lessonIndex}`,
                        name: title || t("courseDetails.learningMaterials"),
                        items: mediaItems,
                    });
                }
            });

            const items = sessions.flatMap((session) => session.items);

            return {
                key: section.id || index,
                name: section.name || `${t("progress.week")} ${index + 1}`,
                sessions,
                items,
            };
        });

    const calculateItemsProgress = (items: any[]) => {
        if (!items || items.length === 0) return 0;

        const total = items.length;
        const completed = items.filter(
            (v: any) => v.status === t("progress.watched")
        ).length;
        return Math.round((completed / total) * 100);
    };

    const totalWeeks = weekData.filter(
        (week: any) => week.items.length > 0
    ).length;
    const completedWeeks = weekData.filter(
        (week: any) => calculateItemsProgress(week.items) === 100
    ).length;

    const theoryProgressPercent = getProgressPercent(
        completedWeeks,
        totalWeeks
    );

    // group-title Bài tập
    const exerciseData = [
        {
            key: `group-${t("progress.quizGroup")}`,
            name: t("progress.quizGroup"),
            status: '',
            isGroup: true,
            progressPercent: quizProgressPercent,
        },
        ...quizFormatted,
        {
            key: `group-${t("progress.assignmentGroup")}`,
            name: t("progress.assignmentGroup"),
            status: '',
            isGroup: true,
            progressPercent: assignProgressPercent,
        },
        ...assignFormatted,
    ];


    // Colums table Bài tập + Hoạt động khác
    const columns: ColumnsType<any> = [
        {
            title: t("progress.activityName"),
            dataIndex: 'name',
            key: 'name',
            width: 400,
            render: (text, record: any) =>
                record.isGroup ? (
                    <div
                        style={{
                            backgroundColor: '#f0f2f5',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: '#1a1a1a',
                            padding: '6px 12px',
                            textTransform: 'uppercase',
                        }}
                    >
                        {record.name}
                    </div>
                ) : (
                    record.name
                ),
        },
        {
            title: t("progress.status"),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 200,
            render: (text, record: any) =>
                record.isGroup ? (
                    <div style={{ padding: '4px 8px' }}>
                        <Progress
                            percent={record.progressPercent || 0}
                            size="small"
                            strokeColor="#52c41a"
                        />
                    </div>
                ) : (
                    <Tag color={text === t("progress.completed") ? "green" : "red"}>{text}</Tag>
                ),
        },
    ];


    // Colums lý thuyết theo tuần
    const weekColumns: ColumnsType<any> = [
        {
            title: t("progress.week"),
            dataIndex: "name",
            key: "name",
            width: 400,
        },
        {
            title: t("progress.progress"),
            dataIndex: "progress",
            key: "progress",
            width: 200,
            align: "center",
            render: (_: any, record: any) => {
                if (!record.items || record.items.length === 0) {
                    return null;
                }
                const percent = calculateItemsProgress(record.items);
                return <Progress percent={percent} size="small" />;
            },
        },
    ];

    const sessionColumns: ColumnsType<any> = [
        {
            title: t("progress.session"),
            dataIndex: "name",
            key: "name",
            width: 400,
        },
        {
            title: t("progress.progress"),
            dataIndex: "progress",
            key: "progress",
            width: 200,
            align: "center",
            render: (_: any, record: any) => {
                if (!record.items || record.items.length === 0) {
                    return null;
                }
                const percent = calculateItemsProgress(record.items);
                return <Progress percent={percent} size="small" />;
            },
        },
    ];

    // Columns for inner table (videos/audio/pdf)
    const materialColumns: ColumnsType<any> = [
        { title: t("progress.materialName"), dataIndex: "name", key: "name", width: 400 },
        {
            title: t("progress.status"),
            dataIndex: "status",
            key: "status",
            width: 93,
            align: "center",
            render: (_: any, item: any) => {
                const watched = t("progress.watched");
                const watching = t("progress.watching");
                const color =
                    item.status === watched
                        ? "green"
                        : item.status === watching
                        ? "orange"
                        : "red";
                return (
                    <div className="d-flex" style={{ alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Tag color={color} className="flex-column">{item.status}</Tag>
                    </div>
                );
            }
        },
        {
            title: t("progress.progress"),
            dataIndex: "percent",
            key: "progress",
            width: 93,
            align: "center",
            render: (_: any, item: any) => {
                if (item.filetype === "pdf") {
                    return null;
                }

                const watched = t("progress.watched");
                const watching = t("progress.watching");
                const color =
                    item.status === watched
                        ? "green"
                        : item.status === watching
                        ? "orange"
                        : "red";
                return (
                    <div className="d-flex" style={{ alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Progress
                            type="circle"
                            percent={item.percent || 0}
                            width={26}
                            strokeColor={color}
                            className="flex-column"
                        />
                    </div>
                );
            }
        }
    ];





    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        headerBg: "#fafafa",          // Màu nền header
                        headerColor: "#000",          // Màu chữ header
                        cellPaddingBlock: 8,         // Padding dọc cell => gián tiếp tăng height
                        cellPaddingInline: 12,        // Padding ngang cell
                    },
                },
            }}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <Breadcrumb title={t("progress.title")} />
                    </div>
                    <h3 className="py-4">{courseDetailData?.fullname}</h3>

                    <div className="mb-5">
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.theory")} </span>
                            <Progress
                                percent={theoryProgressPercent}
                                style={{ width: '40%', height: 10 }}
                                strokeColor={
                                    theoryProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                }
                            />
                        </h5>
                        <Table
                            columns={weekColumns}
                            dataSource={weekData}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}

                            expandable={{
                                expandedRowKeys,
                                onExpand: (expanded, record) => {
                                    if (expanded) {
                                        setExpandedRowKeys([record.key]);
                                    } else {
                                        setExpandedRowKeys([]);
                                    }
                                },
                                expandedRowRender: (record) => (
                                    <Table<any>
                                        columns={sessionColumns}
                                        dataSource={record.sessions}
                                        pagination={false}
                                        size="small"
                                        expandable={{
                                            expandedRowRender: (session) => (
                                                <Table<any>
                                                    columns={materialColumns}
                                                    dataSource={session.items.map(
                                                        (item: any, i: number) => ({
                                                            key: `${session.key}-item-${i}`,
                                                            ...item,
                                                        })
                                                    )}
                                                    pagination={false}
                                                    size="small"
                                                />
                                            ),
                                            rowExpandable: (session) =>
                                                session.items?.length > 0,
                                        }}
                                    />
                                ),
                                rowExpandable: (record) =>
                                    record.sessions?.length > 0,
                            }}
                        />
                    </div>

                    <div className="mb-5">
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.exercises")}</span>
                            <Progress
                                percent={exerciseProgressPercent}
                                style={{ width: '40%' }}
                                strokeColor={
                                    exerciseProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                }
                            />
                        </h5>
                        <Table
                            columns={columns}
                            dataSource={exerciseData}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}
                        />
                    </div>

                    <div>
                        <h5 className="mb-2 d-flex justify-content-between">
                            <span>{t("progress.otherActivities")}</span>
                            <Progress
                                percent={otherProgressPercent}
                                style={{ width: '40%' }}
                                strokeColor={
                                    exerciseProgressPercent === 100
                                        ? "#52c41a" // 100% thì toàn bộ màu xanh
                                        : {
                                            "0%": "#ff4d4f",
                                            "40%": "#faad14",
                                            "90%": "#52c41a",
                                        }
                                } />
                        </h5>
                        <Table
                            columns={columns}
                            dataSource={formatOtherModules(countOtherModules)}
                            pagination={false}
                            bordered
                            scroll={{ x: 'max-content' }}
                        />
                    </div>
                </div>
            </div>
        </ConfigProvider>

    );
};

export default StudentProgressDetail;
